<template>
  <div class="inspection-screen">
    <page-header :title="$t('app.inspections.title')" />
    <div class="row mt-20">
      <div class="col-sm-6">
        <search-input :placeholder="$t('app.inspections.search_input_placeholder')" @input="search" />
      </div>
      <div class="col-sm-6 text-right finalization-row">
        <base-button v-if="hasPermission && baseStateStatus && baseStateStatus === 'finalized' && selectedSite !== null"
          :title="$t('app.inspections.create_title')" classNames="btn btn-orange rounded pull-right"
          @clicked="clickedCreateInpsection" />
        <span style="font-size: 16px;"
          v-if="hasPermission && baseStateStatus && baseStateStatus !== 'finalized'">{{ $t('app.inspections.needToFinalize') }}</span>
      </div>
    </div>
    <div class="row mt-30">
      <div class="col-sm-3">
        <base-dropdown
          :list="sites"
          v-model="selectedSite"
          :defaultValue="selectedSite"
          :inputDisabled="loggedInUser.site_id != null"
          labelClassNames="control-label mb-10 text-left"
          :labelValue="$t(`app.inspections.filter.site_id`)"
          @input="filterBySite"/>
      </div>
    </div>
    <configurable-list :loadingText="loadingText" v-if="allInspections && allInspections.inspections"
      :isNeedSearch="false"
      class="inspections-list"
      :title="$t('app.inspections.title')"
      :data="allInspections.inspections"
      :fields="hasPermission ? adminFields : fields"
      @actionMade="actionMade"
      @orderTable="orderTable" />
    <paginator-component :totalPages="totalPages" :currentPageIndex="currentPageIndex" @itemSelected="itemSelected" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ConfigurableList from '@/components/shared/lists/ConfigurableList';
import Paginator from '@/components/shared/navigations/Paginator';
import BaseButton from '@/components/shared/buttons/BaseButton';
import BaseDropdown from '@/components/shared/inputs/BaseDropDown';
import SearchInput from '@/components/shared/inputs/SearchInput';
import PageHeader from '@/components/shared/layouts/PageHeader';

export default {
  name: 'InspectionsScreen',
  components: {
    'search-input': SearchInput,
    'base-button': BaseButton,
    'configurable-list': ConfigurableList,
    'paginator-component': Paginator,
    'page-header': PageHeader,
    BaseDropdown,
  },
  props: {},
  data() {
    return {
      order: 'created_at',
      direction: 'ASC',
      currentPageIndex: 1,
      searchTerm: '',
      selectedSite: null,
      sites: [],
      baseStateStatus: undefined,
      allSite: undefined,
    };
  },
  created() {
    this.fetchSites({
      partnerId: this.loggedInUser.partner_id,
      pageIndex: null,
      searchTerm: '',
      orderInfo: {
        order: 'created_at',
        direction: 'ASC',
      },
      isMobile: true,
    }).then((response) => {
      if (!this.hasPermission) {
        this.sites = response.filter(x => x.id === this.loggedInUser.site_id).map(x => ({ value: x.id, text: this.$t(x.name) }));
        this.baseStateStatus = this.sites[0].base_state_status;
        this.allSite = response;
        this.selectedSite = this.loggedInUser.site_id;
      } else {
        this.baseStateStatus = response[0].base_state_status;
        this.sites = response.map(x => ({ value: x.id, text: this.$t(x.name) }));
        this.allSite = response;
        if (this.sites.length > 0) {
          this.selectedSite = this.sites[0].value
        }
      }
      if (this.loggedInUser.role === 'worker') {
        this.fetchInspections({
          pageIndex: this.currentPageIndex,
          searchTerm: this.searchTerm,
          orderInfo: null,
          forWorker: true,
          siteId: this.selectedSite,
        });
      } else {
        this.fetchInspections({
          pageIndex: this.currentPageIndex,
          searchTerm: this.searchTerm,
          siteId: this.selectedSite,
        });
      }
    });
  },
  computed: {
    ...mapGetters({
      allInspections: 'inspections/allInspections',
      loggedInUser: 'loggedInUser',
      commonData: 'partners/commonData',
    }),
    hasPermission() {
      return this.loggedInUser.site_id === null;
    },
    fields() {
      return [
        {
          title: this.$t('app.tables.inspections.identifier'),
          cellClass: 'identifier-cell',
          key: 'identifier',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.name'),
          cellClass: 'name-cell',
          key: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.responsible_user'),
          cellClass: 'responsible-user-cell',
          key: 'responsible_user',
          secondAttribute: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.maturity'),
          cellClass: 'maturity-cell',
          isDate: true,
          key: 'date_of_maturity',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.status'),
          key: 'status',
          cellClass: 'status-cell',
          class: 'label label',
          subClass: 'status',
          orderable: true,
          withTranslation: true,
          translation: 'app.inspections.statuses',
        },
        {
          title: this.$t('app.tables.global.actions'),
          isAction: true,
          key: 'memo',
          cellClass: 'edit-note-cell',
          class: 'ti-notepad',
          checkForStatus: true,
          status: 'completed',
        },
        {
          title: '',
          isAction: true,
          key: 'edit',
          cellClass: 'edit-cell',
          class: 'zmdi zmdi-edit',
          checkForStatus: false,
          status: 'completed',
        },
        {
          title: '',
          isAction: true,
          key: 'delete',
          cellClass: 'delete-cell',
          class: 'zmdi zmdi-delete',
          disabled: !this.hasPermission
        },
      ];
    },
    adminFields() {
      return [
        {
          title: this.$t('app.tables.inspections.identifier'),
          cellClass: 'identifier-cell',
          key: 'identifier',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.name'),
          cellClass: 'name-cell',
          key: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.responsible_user'),
          cellClass: 'responsible-user-cell',
          key: 'responsible_user',
          secondAttribute: 'name',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.maturity'),
          cellClass: 'maturity-cell',
          isDate: true,
          key: 'date_of_maturity',
          orderable: true,
        },
        {
          title: this.$t('app.tables.inspections.status'),
          key: 'status',
          cellClass: 'status-cell',
          class: 'label label',
          subClass: 'status',
          orderable: true,
          withTranslation: true,
          translation: 'app.inspections.statuses',
        },
        {
          title: this.$t('app.tables.global.actions'),
          isAction: true,
          key: 'memo',
          cellClass: 'edit-note-cell',
          class: 'ti-notepad',
          checkForStatus: true,
          status: 'completed',
        },
        {
          title: '',
          isAction: true,
          key: 'edit',
          cellClass: 'edit-cell',
          class: 'zmdi zmdi-edit',
          checkForStatus: false,
          status: 'completed',
        },
        {
          title: '',
          isAction: true,
          key: 'delete',
          cellClass: 'delete-cell',
          class: 'zmdi zmdi-delete',
        },
      ];
    },
    totalPages() {
      return this.allInspections.total_pages || 0;
    },
    loadingText() {
      if (this.allInspections && this.allInspections.inspections.length === 0) {
        return this.$t('app.tasks.empty_list');
      }
      return this.$t('app.listLoading');
    },
  },
  methods: {
    ...mapActions({
      fetchInspections: 'inspections/fetchInspections',
      updateInspectionParams: 'inspections/updateInspection',
      deleteInspection: 'inspections/deleteInspection',
      fetchInspection: 'inspections/fetchInspection',
      fetchSites: 'sites/fetchSites',
    }),
    search(keyword) {
      this.searchTerm = keyword;
      this.currentPageIndex = 1;
      this.fetchInspections({
        pageIndex: this.currentPageIndex,
        searchTerm: this.searchTerm,
        siteId: this.selectedSite,
      });
    },
    filterBySite() {
      this.currentPageIndex = 1;
      this.fetchInspections({
        pageIndex: this.currentPageIndex,
        searchTerm: this.searchTerm,
        orderInfo: {
          order: this.order,
          direction: this.direction,
        },
        forWorker: this.loggedInUser.role === 'worker',
        siteId: this.selectedSite,
      });
    },
    itemSelected(item) {
      this.currentPageIndex = item;
    },
    clickedCreateInpsection() {
      this.$router.push({ path: '/inspections/create', query: { siteId: this.selectedSite } });
    },
    actionMade(payload) {
      // eslint-disable-next-line default-case
      switch (payload.actionType) {
        case 'edit': {
          this.fetchInspection({ id: payload.id, withDeleted: false }).then((response) => {
            if (response.status === 'pending') {
              this.updateInspectionParams(null);
              this.$router.push({
                name: 'InspectionEditScreen',
                params: { id: payload.id },
                query: { siteId: this.selectedSite },
              });
            } else {
              this.updateInspectionParams(null);
              this.$router.push({
                name: 'InspectionFinalisationScreen',
                params: { id: payload.id },
                query: { siteId: this.selectedSite },
              });
            }
          });
          break;
        }
        case 'delete': {
          this.$swal
            .fire({
              html: this.$t('app.notifications.general_confirm.title'),
              type: 'info',
              showCloseButton: true,
              showCancelButton: true,
              focusConfirm: false,
              confirmButtonText: this.$t('app.notifications.general_confirm.ok'),
              cancelButtonText: this.$t('app.notifications.general_confirm.cancel'),
            })
            .then((result) => {
              if (result.value) {
                const previousTotalPages = this.totalPages;
                this.deleteInspection(payload.id)
                  .then(() => {
                    this.$eventBus.showNotification({
                      notificationText: this.$t('app.inspections.delete'),
                      notificationType: 'success',
                    });
                    this.fetchInspections({
                      pageIndex: this.currentPageIndex,
                      searchTerm: this.searchTerm,
                      siteId: this.selectedSite,
                    }).then(() => {
                      if (this.totalPages < previousTotalPages) {
                        this.currentPageIndex = this.totalPages;
                      }
                    });
                  })
                  .catch((error) => {
                    this.$swal
                      .fire({
                        html: error[0],
                        type: 'error',
                        showCloseButton: true,
                        showCancelButton: false,
                        showConfirmButton: false,
                        focusConfirm: false,
                      });
                  });
              }
            });
          break;
        }
        case 'memo': {
          this.updateInspectionParams(null);
          this.$router.push({
            name: 'InspectionReportScreen',
            params: { id: payload.id },
          });
          break;
        }
      }
    },
    orderTable(payload) {
      this.order = payload.order;
      this.direction = payload.direction;

      this.fetchInspections({
        pageIndex: this.currentPageIndex,
        searchTerm: this.searchTerm,
        orderInfo: {
          order: payload.order,
          direction: payload.direction,
        },
        siteId: this.selectedSite,
      });
    },
  },
  watch: {
    currentPageIndex(newValue) {
      if (this.order !== undefined && this.direction !== undefined) {
        this.fetchInspections({
          pageIndex: newValue,
          searchTerm: this.searchTerm,
          orderInfo: {
            order: this.order,
            direction: this.direction,
          },
          siteId: this.selectedSite,
        });
      } else {
        this.fetchInspections({
          pageIndex: newValue,
          searchTerm: this.searchTerm,
          siteId: this.selectedSite,
        });
      }
    },
    selectedSite(newValue) {
      this.baseStateStatus = this.allSite.filter(x => x.id === newValue)[0].base_state_status;
    },
  },
};
</script>

<style lang="scss">
.inspection-screen {
  .finalization-row {
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .rounded {
    border-radius: 4px;
  }

  .inspections-list {
    padding: 10px;
    margin-top: 10px;
  }

  .label {
    padding: 6px 30px 6px 30px;
    float: left;
    min-width: 130px;
    background: #d9534f;
  }

  .label-ready {
    padding: 6px 30px 6px 30px;
    float: left;
    min-width: 130px;
    background: #ffcd44;
  }

  .label-completed {
    background: #22af47;
    width: 100%;
  }

  .label-done {
    background: #5bc0de;
  }

  .identifier-cell {
    width: 15%;

    @media (max-width: 1094px) {
      width: 15%;
    }
  }

  .name-cell {
    width: 15%;

    @media (max-width: 1471px) {
      width: 15%;
    }

    @media (max-width: 1175px) {
      width: 10%;
    }
  }

  .responsible-user-cell {
    width: 15%;
    text-align: center;
  }

  .responsible-user-cell-header {
    justify-content: center;
    padding-left: 26px;
  }

  .maturity-cell {
    width: 12%;
    text-align: center;
  }

  .maturity-cell-header {
    justify-content: center;
    padding-left: 26px;
  }

  .status-cell {
    width: 15%;
    padding: 0 50px 0 50px;
  }

  .status-cell-header {
    justify-content: center;
    padding-left: 26px;
  }

  .note-cell {
    width: 8%;
  }

  td.note-cell {
    padding-left: 2.5%;
  }

  .edit-note-cell {
    width: 3%;
  }

  td.edit-note-cell {
    padding-left: 2.6%;

    @media (max-width: 1471px) {
      padding-left: 3%;
    }
  }

  .edit-cell {
    width: 3%;
  }

  .delete-cell {
    width: 3%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/modules/inspections/InspectionsScreen.vue